// DueTodayNotificationManager.jsx
import React, { useState, useEffect, useRef } from "react";
import DueTodayNotification from "./DueTodayNotification";
import apiService from "../../services/api";

// Use localStorage to persist dismissed tasks across refreshes
const DISMISSED_TASKS_KEY = "due_today_dismissed_tasks";

const getDismissedTasks = () => {
  try {
    const stored = localStorage.getItem(DISMISSED_TASKS_KEY);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch (error) {
    console.warn("Failed to load dismissed tasks from localStorage", error);
    return new Set();
  }
};

const saveDismissedTasks = (dismissedSet) => {
  try {
    localStorage.setItem(
      DISMISSED_TASKS_KEY,
      JSON.stringify(Array.from(dismissedSet)),
    );
  } catch (error) {
    console.warn("Failed to save dismissed tasks to localStorage", error);
  }
};

const DueTodayNotificationManager = () => {
  const [tasks, setTasks] = useState([]);
  const hasFetched = useRef(false);

  // Initialize dismissed set from localStorage
  const dismissedThisSession = useRef(getDismissedTasks());

  useEffect(() => {
    // Only fetch once per mount
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchDueTodayTasks = async () => {
      try {
        const response = await apiService.getTasks({
          myTasks: true,
          dueToday: true,
          status: "", // all statuses except completed — filtered below
          limit: 10,
        });

        const allTasks = response?.data || [];

        // Filter out completed tasks and tasks that have been dismissed
        const pending = allTasks.filter(
          (t) =>
            t.status !== "completed" && !dismissedThisSession.current.has(t.id),
        );

        setTasks(pending);
      } catch (error) {
        // Fail silently — this is a non-critical UI enhancement
        console.warn(
          "DueTodayNotificationManager: failed to load tasks",
          error,
        );
      }
    };

    fetchDueTodayTasks();
  }, []);

  const handleDismiss = (taskId) => {
    // Add to dismissed set
    dismissedThisSession.current.add(taskId);

    // Persist to localStorage
    saveDismissedTasks(dismissedThisSession.current);

    // Remove from UI
    setTasks((prev) => prev.filter((t) => t.id !== taskId));
  };

  if (tasks.length === 0) return null;

  return (
    <div
      className="fixed top-4 right-4 z-[99998] flex flex-col gap-3 pointer-events-none"
      style={{ maxWidth: "420px", width: "calc(100vw - 2rem)" }}
    >
      {tasks.map((task, index) => (
        <div key={task.id} className="pointer-events-auto">
          <DueTodayNotification
            task={task}
            index={index}
            onDismiss={handleDismiss}
          />
        </div>
      ))}
    </div>
  );
};

export default DueTodayNotificationManager;
